import React from 'react';
import { SESSION_TIMEZONE_OPTIONS, formatTimezoneOffset } from './sessionFormOptions';

type Props = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  compact?: boolean;
};

const timezoneShortLabel = (timezone: string): string => {
  const parts = timezone.split('/');
  return parts[parts.length - 1]?.replace(/_/g, ' ') ?? timezone;
};

const timezoneOffsetBadge = (timezone: string): string => {
  const offset = formatTimezoneOffset(timezone);
  if (!offset) return 'TZ';
  const match = offset.match(/([+-]\d+(?::\d+)?)/);
  if (match) return match[1];
  if (offset.includes('UTC')) return 'UTC';
  return offset.slice(0, 4);
};

export const SessionTimezonePicker: React.FC<Props> = ({
  value,
  onChange,
  label = 'Fuseau horaire',
  compact = false,
}) => {
  // Le fuseau courant peut venir d'une ville hors liste (ex. Africa/Dakar) : on l'affiche en tête.
  const options: string[] =
    value && !(SESSION_TIMEZONE_OPTIONS as readonly string[]).includes(value)
      ? [value, ...SESSION_TIMEZONE_OPTIONS]
      : [...SESSION_TIMEZONE_OPTIONS];

  return (
  <div>
    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">{label}</label>
    <div
      className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scroll-smooth snap-x snap-mandatory [scrollbar-width:thin]"
      role="listbox"
      aria-label={label}
    >
      {options.map((timezone) => {
        const selected = value === timezone;
        const offset = formatTimezoneOffset(timezone);
        const shortLabel = timezoneShortLabel(timezone);
        const offsetBadge = timezoneOffsetBadge(timezone);

        return (
          <label
            key={timezone}
            className={[
              'flex shrink-0 cursor-pointer snap-start items-center gap-2.5 rounded-2xl border-2 px-3.5 transition-all',
              compact ? 'min-w-[148px] py-2.5' : 'min-w-[160px] py-3',
              selected
                ? 'border-indigo-500 bg-indigo-50/80 shadow-sm shadow-indigo-100'
                : 'border-slate-100 bg-slate-50 hover:border-slate-200 hover:bg-white',
            ].join(' ')}
          >
            <input
              type="radio"
              name="session_timezone"
              value={timezone}
              checked={selected}
              onChange={() => onChange(timezone)}
              className="sr-only"
            />
            <span
              className={[
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[10px] font-black uppercase leading-none',
                selected ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700',
              ].join(' ')}
            >
              {offsetBadge}
            </span>
            <span className="min-w-0">
              <span className={`block font-bold text-slate-900 ${compact ? 'text-sm' : 'text-sm'}`}>{shortLabel}</span>
              <span className="block text-xs text-slate-500">
                {offset || timezone.split('/').pop()?.replace(/_/g, ' ')}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  </div>
  );
};
